import { KeyObjectKind, KeyObjectLink, opensObject, typeObjectLinks } from '../../Helpers/keyRegisterUtils';
import { sortedUnits, unitDisplayName } from '../../Helpers/unitUtils';
import { sortedStorageUnits, storageUnitDisplayName } from '../../Helpers/storageUnitUtils';
import { KeyType, KeyTypeObjectLinks } from '../../types/keyRegister';
import { Unit } from '../../types/units';
import { StorageUnit } from '../../types/storageUnits';
import { UnitKind } from '../../types/Enums';

/**
 * The lägenheter, lokaler and förråd a key type can open (`opensUnits`, `opensStorageUnits`),
 * as read from one building's `units` and `storageUnits`.
 */

export type KeyObjectGroup = 'Lägenheter' | 'Lokaler' | 'Förråd';
export const KEY_OBJECT_GROUPS: KeyObjectGroup[] = ['Lägenheter', 'Lokaler', 'Förråd'];

export interface KeyObject {
    kind: KeyObjectKind;
    key: string;
    buildingKey: string;
    /** "Lägenhet 1001", "Lokal 3", "Förråd 12". */
    name: string;
    group: KeyObjectGroup;
}

/** A building's objects: lägenheter, then lokaler, then förråd, each in number order. */
export const buildingKeyObjects = (
    buildingKey: string,
    units: { [unitKey: string]: Unit },
    storageUnits: { [unitKey: string]: StorageUnit }
): KeyObject[] => [
    ...sortedUnits(units).map(unit => ({
        kind: 'unit' as const,
        key: unit.key,
        buildingKey,
        name: unitDisplayName(unit),
        group: (unit.kind === UnitKind.Commercial ? 'Lokaler' : 'Lägenheter') as KeyObjectGroup,
    })),
    ...sortedStorageUnits(storageUnits).map(unit => ({
        kind: 'storageUnit' as const,
        key: unit.key,
        buildingKey,
        name: storageUnitDisplayName(unit),
        group: 'Förråd' as const,
    })),
];

/** A link as the form holds it. */
export type KeyObjectRef = Pick<KeyObject, 'kind' | 'key' | 'buildingKey'>;

export type KeyObjectsByBuilding = { [buildingKey: string]: KeyObject[] };

/** The linked objects that still exist, in building order of the links, each building's objects in their own order. */
export const resolveObjectLinks = (links: KeyObjectLink[], objectsByBuilding: KeyObjectsByBuilding): KeyObject[] => {
    const wanted = new Set(links.map(link => `${link.kind}/${link.objectKey}`));
    const buildingOrder = Array.from(new Set(links.map(link => link.buildingKey)));
    return buildingOrder.flatMap(buildingKey =>
        (objectsByBuilding[buildingKey] ?? []).filter(object => wanted.has(`${object.kind}/${object.key}`)));
};

/** How many objects the type row names before "+N till". */
export const OBJECT_LABEL_LIMIT = 3;

/**
 * "Hus B: Lägenhet 1001, Förråd 12 · Hus C: Lokal 3", at most `OBJECT_LABEL_LIMIT` names, then "+N till".
 * Objects not (yet) loaded and dangling links are left out.
 */
export const objectLinksLabel = (
    type: Pick<KeyType, 'opensUnits' | 'opensStorageUnits'>,
    objectsByBuilding: KeyObjectsByBuilding,
    buildingName: (buildingKey: string) => string | undefined
): string | undefined => {
    const objects = resolveObjectLinks(typeObjectLinks(type), objectsByBuilding);
    if (objects.length === 0) return undefined;
    const shown = objects.slice(0, OBJECT_LABEL_LIMIT);
    const parts: string[] = [];
    for (const buildingKey of Array.from(new Set(shown.map(o => o.buildingKey)))) {
        const names = shown.filter(o => o.buildingKey === buildingKey).map(o => o.name).join(', ');
        const building = buildingName(buildingKey);
        parts.push(building ? `${building}: ${names}` : names);
    }
    const rest = objects.length - shown.length;
    return parts.join(' · ') + (rest > 0 ? ` +${rest} till` : '');
};

/** The form's picked objects back into the two stored maps. */
export const toObjectLinkMaps = (objects: KeyObjectRef[]): { opensUnits: KeyTypeObjectLinks; opensStorageUnits: KeyTypeObjectLinks } => {
    const opensUnits: KeyTypeObjectLinks = {};
    const opensStorageUnits: KeyTypeObjectLinks = {};
    for (const object of objects) {
        (object.kind === 'unit' ? opensUnits : opensStorageUnits)[object.key] = object.buildingKey;
    }
    return { opensUnits, opensStorageUnits };
};

/** The stored maps as form values. */
export const objectRefsOf = (type: Pick<KeyType, 'opensUnits' | 'opensStorageUnits'>): KeyObjectRef[] =>
    typeObjectLinks(type).map(link => ({ kind: link.kind, key: link.objectKey, buildingKey: link.buildingKey }));

export interface ObjectKeyTypes {
    /** Linked to the object itself. */
    direct: KeyType[];
    /** Opening the object's building as a whole (port, tvättstuga), not already in `direct`. */
    building: KeyType[];
}

/** The key types shown in an object's Nycklar modal, A–Ö; archived types are left out. */
export const objectKeyTypes = (types: KeyType[], object: KeyObjectRef): ObjectKeyTypes => {
    const live = types
        .filter(type => !type.archived)
        .sort((a, b) => a.name.localeCompare(b.name, 'sv', { numeric: true }));
    const direct = live.filter(type => opensObject(type, object.key));
    return {
        direct,
        building: live.filter(type => !direct.includes(type) && !!type.opensBuildings?.[object.buildingKey]),
    };
};
